import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  DEFAULT_WINDOW_SIZE,
  EXPIRY_CHECK_INTERVAL_MS,
  EXPIRY_MS,
  IMPERSONATION_APP_EVENTS,
  IMPERSONATION_USER_EVENTS,
  MAX_SESSIONS,
} from "../constants";
import type { ImpersonationSession } from "../types";
import { buildImpersonationUrl } from "../utils/buildImpersonationUrl";
import {
  generateSessionId,
  getInitialBalloonPosition,
  getInitialWindowPosition,
  loadPersistedSessions,
  nextZIndex,
  persistSessions,
} from "../utils/sessionHelpers";
import { useViewportClamp } from "./useViewportClamp";

export function useImpersonationSessionsHook() {
  const { adminUserId: employeeId } = useSession();
  const [sessions, setSessions] = useState<ImpersonationSession[]>(loadPersistedSessions);
  const sessionsRef = useRef(sessions);
  const isInitialMount = useRef(true);

  useLayoutEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // Persist sessions to localStorage on every change (skip initial load)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    persistSessions(sessions);
  }, [sessions]);

  const openSession = useCallback(
    (workerId: string, workerName: string, cognitoUsername: string) => {
      let event: { name: string; sessionId: string } | "limit" | undefined;

      setSessions((previous) => {
        const existing = previous.find((session) => session.workerId === workerId);
        if (existing) {
          const zIndex = nextZIndex();
          event = { name: "restored", sessionId: existing.id };
          return previous.map((session) =>
            session.id === existing.id
              ? { ...session, isMinimized: false, isActive: true, zIndex }
              : session
          );
        }

        if (previous.length >= MAX_SESSIONS) {
          event = "limit";
          return previous;
        }

        const impersonateUrl = buildImpersonationUrl(workerId, cognitoUsername);

        const newSession: ImpersonationSession = {
          id: generateSessionId(),
          workerId,
          workerName,
          cognitoUsername,
          url: impersonateUrl,
          startedAt: Date.now(),
          isMinimized: false,
          isActive: true,
          position: getInitialWindowPosition(previous.length),
          size: { ...DEFAULT_WINDOW_SIZE },
          balloonPosition: getInitialBalloonPosition(previous.length),
          zIndex: nextZIndex(),
        };

        event = { name: "opened", sessionId: newSession.id };
        return [...previous, newSession];
      });

      if (event === "limit") {
        logEvent(IMPERSONATION_APP_EVENTS.SESSION_LIMIT_REACHED, {
          employeeId,
          workerId,
          workerName,
        });
        showErrorToast(`Maximum ${MAX_SESSIONS} impersonation sessions. Close one first.`);
      } else if (event?.name === "restored") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          workerId,
          workerName,
          sessionId: event.sessionId,
        });
      } else if (event?.name === "opened") {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_STARTED, {
          employeeId,
          workerId,
          workerName,
          sessionId: event.sessionId,
        });
      }
    },
    [employeeId]
  );

  const closeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_CLOSED, {
          employeeId,
          workerId: session.workerId,
          workerName: session.workerName,
          sessionId: id,
        });
      }

      setSessions((previous) => previous.filter((s) => s.id !== id));
    },
    [employeeId]
  );

  const minimizeSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_MINIMIZED, {
          employeeId,
          workerId: session.workerId,
          workerName: session.workerName,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: true } : s))
      );
    },
    [employeeId]
  );

  const restoreSession = useCallback(
    (id: string) => {
      const session = sessionsRef.current.find((s) => s.id === id);
      if (session) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_RESTORED, {
          employeeId,
          workerId: session.workerId,
          workerName: session.workerName,
          sessionId: id,
        });
      }

      setSessions((previous) =>
        previous.map((s) => (s.id === id ? { ...s, isMinimized: false, isActive: true } : s))
      );
    },
    [employeeId]
  );

  const updateBalloonPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setSessions((previous) =>
      previous.map((session) =>
        session.id === id ? { ...session, balloonPosition: position } : session
      )
    );
  }, []);

  const updateWindowPosition = useCallback((id: string, position: { x: number; y: number }) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, position } : session))
    );
  }, []);

  const updateWindowSize = useCallback((id: string, size: { width: number; height: number }) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, size } : session))
    );
  }, []);

  const bringToFront = useCallback((id: string) => {
    const zIndex = nextZIndex();
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, zIndex } : session))
    );
  }, []);

  const updateSessionExpiresAt = useCallback((id: string, expiresAt: number) => {
    setSessions((previous) =>
      previous.map((session) => (session.id === id ? { ...session, expiresAt } : session))
    );
  }, []);

  // Deactivate expired sessions (close iframe but keep pill)
  useEffect(() => {
    if (sessions.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const expiredSessions: Array<{
        employeeId: string | undefined;
        workerId: string;
        workerName: string;
        sessionId: string;
      }> = [];

      setSessions((previous) =>
        previous.map((session) => {
          const isExpired = session.expiresAt
            ? now >= session.expiresAt
            : now - session.startedAt >= EXPIRY_MS;
          if (isExpired && session.isActive) {
            expiredSessions.push({
              employeeId,
              workerId: session.workerId,
              workerName: session.workerName,
              sessionId: session.id,
            });
            return { ...session, isActive: false, isMinimized: true };
          }

          return session;
        })
      );

      for (const expired of expiredSessions) {
        logEvent(IMPERSONATION_USER_EVENTS.SESSION_EXPIRED, expired);
      }
    }, EXPIRY_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
  }, [employeeId, sessions.length]);

  useViewportClamp(sessions.length, setSessions);

  return {
    sessions,
    openSession,
    closeSession,
    minimizeSession,
    restoreSession,
    bringToFront,
    updateBalloonPosition,
    updateSessionExpiresAt,
    updateWindowPosition,
    updateWindowSize,
  };
}
